export type Point3 = [number, number, number];
export type Point2 = [number, number];

export const LOOP_DURATION = 38000;
export const STEP_OFFSETS = [0, 7000, 17000, 22000];
const STEP_DURATIONS = [7000, 10000, 5000, 16000];
export const FOCUS_STEP = 3;
export const FOCUS_STAGES = {
    intro: 0,
    orbit: 1,
    pan: 2,
    zoom: 3,
    hold: 4,
    return: 5,
};
const FOCUS_STAGE_BOUNDS = [0, 2000, 5000, 8000, 12000, 14000, 16000];
const FOCUS_ORBIT_YAW = 0.45;
const FOCUS_ORBIT_PITCH = 0.24;
const FOCUS_ZOOM_DISTANCE = 5.1;
const FOCUS_FADE_DURATION = 350;
const FOCUS_GESTURE_SCALE = 0.75;
export const FOCUS_TARGET: Point3 = [0.645, 0.365, -0.43];
const VIEW_CENTRE: Point2 = [200, 124];
const REST_YAW = 0.53;
const REST_PITCH = 0.44;
const REST_DISTANCE = 7.8;
const VIEWBOX_WIDTH = 400;
const FOCAL = 530;

export const LIMITS = {
    minPitch: 0.08,
    maxPitch: 1.35,
    minDistance: 4.5,
    maxDistance: 12,
    maxPan: 1.5,
};

export const GESTURE_STEPS = {orbit: 0, pan: 1, zoom: 2};

export type Gesture = keyof typeof GESTURE_STEPS;

export interface StepState {
    index: number;
    phase: number;
    depth: boolean;
    motion: number;
    stage: number;
}

export interface GestureState {
    index: number;
    motion: number;
    depth: boolean;
    panVector?: Point2;
    note?: string;
    fade: number;
}

export interface Camera {
    yaw: number;
    pitch: number;
    dx: number;
    dy: number;
    dz: number;
    distance: number;
}

export interface Shape {
    tag: 'polygon' | 'polyline';
    points: string;
    className: string;
    dashed?: boolean;
}

const wave = (t: number) =>
    Math.sin(2 * Math.PI * Math.max(0, Math.min(1, (t - 0.1) / 0.8)));

const smooth = (t: number) => {
    const p = Math.max(0, Math.min(1, t));

    return p * p * (3 - 2 * p);
};

export const getStepOffset = (index: number) => STEP_OFFSETS.at(index) ?? 0;

export const getStepState = (elapsed: number): StepState => {
    const time = ((elapsed % LOOP_DURATION) + LOOP_DURATION) % LOOP_DURATION;
    const index = STEP_OFFSETS.filter((offset) => time >= offset).length - 1;
    const stepTime = time - getStepOffset(index);
    const phase = stepTime / (STEP_DURATIONS.at(index) ?? 1);
    const depth = index === 1 && phase >= 0.5;
    const motion = wave(index === 1 ? (phase * 2) % 1 : phase);
    const stage =
        index === FOCUS_STEP
            ? FOCUS_STAGE_BOUNDS.filter((bound) => stepTime >= bound).length - 1
            : 0;

    return {index, phase, depth, motion, stage};
};

const getFocusTime = (phase: number) =>
    phase * (STEP_DURATIONS.at(FOCUS_STEP) ?? 1);

const getStageBounds = (stage: number) => ({
    start: FOCUS_STAGE_BOUNDS.at(stage) ?? 0,
    end: FOCUS_STAGE_BOUNDS.at(stage + 1) ?? 0,
});

const getStageProgress = (time: number, stage: number) => {
    const {start, end} = getStageBounds(stage);

    return smooth((time - start) / (end - start));
};

const getFocusProgress = (phase: number) => {
    const time = getFocusTime(phase);
    const remaining = 1 - getStageProgress(time, FOCUS_STAGES.return);

    return {
        orbit: getStageProgress(time, FOCUS_STAGES.orbit) * remaining,
        pan: getStageProgress(time, FOCUS_STAGES.pan) * remaining,
        zoom: getStageProgress(time, FOCUS_STAGES.zoom) * remaining,
    };
};

const getFocusCamera = (phase: number): Camera => {
    const {orbit, pan, zoom} = getFocusProgress(phase);
    const yaw = REST_YAW - orbit * FOCUS_ORBIT_YAW;
    const pitch = REST_PITCH - orbit * FOCUS_ORBIT_PITCH;
    const [tx, ty, tz] = FOCUS_TARGET;
    const x = tx * Math.cos(yaw) - tz * Math.sin(yaw);
    const z = tx * Math.sin(yaw) + tz * Math.cos(yaw);
    const vy = (ty - 0.65) * Math.cos(pitch) - z * Math.sin(pitch);

    return {
        yaw,
        pitch,
        dx: -x * pan,
        dy: -vy * pan,
        dz: 0,
        distance: REST_DISTANCE - zoom * FOCUS_ZOOM_DISTANCE,
    };
};

const getStepCamera = ({index, phase, depth, motion}: StepState): Camera => ({
    yaw: REST_YAW - (index === 0 ? motion * 0.66 : 0),
    pitch:
        REST_PITCH + (index === 0 ? Math.sin(phase * Math.PI * 2) * 0.08 : 0),
    dx: index === 1 && !depth ? motion * 0.7 : 0,
    dy: 0,
    dz: depth ? -motion * 0.85 : 0,
    distance: REST_DISTANCE - (index === 2 ? motion * 1.45 : 0),
});

export const getCamera = (step: StepState): Camera =>
    step.index === FOCUS_STEP
        ? getFocusCamera(step.phase)
        : getStepCamera(step);

const FOCUS_NOTES = [
    'Orbit → Pan → Zoom',
    '',
    '',
    '',
    'A closer look at your cabinet',
    'Returning to the room',
];

const getFocusFade = (phase: number, stage: number) => {
    const time = getFocusTime(phase);
    const {start, end} = getStageBounds(stage);

    return smooth(Math.min(time - start, end - time) / FOCUS_FADE_DURATION);
};

const getFocusGesture = (step: StepState): Omit<GestureState, 'fade'> => {
    const {index, stage, phase} = step;
    const progress = getFocusProgress(phase);

    if (stage === FOCUS_STAGES.orbit) {
        return {
            index: GESTURE_STEPS.orbit,
            motion: progress.orbit,
            depth: false,
        };
    }

    if (stage === FOCUS_STAGES.pan) {
        const [targetX, targetY] = project(FOCUS_TARGET, {
            ...getCamera(step),
            dx: 0,
            dy: 0,
        });

        return {
            index: GESTURE_STEPS.pan,
            motion: progress.pan,
            depth: false,
            panVector: [
                (VIEW_CENTRE[0] - targetX) * FOCUS_GESTURE_SCALE,
                (VIEW_CENTRE[1] - targetY) * FOCUS_GESTURE_SCALE,
            ],
        };
    }

    if (stage === FOCUS_STAGES.zoom) {
        return {
            index: GESTURE_STEPS.zoom,
            motion: -0.8 + progress.zoom * 1.6,
            depth: false,
        };
    }

    return {index, motion: 0, depth: false, note: FOCUS_NOTES.at(stage)};
};

export const getGestureState = (step: StepState): GestureState => {
    if (step.index !== FOCUS_STEP) {
        return {
            index: step.index,
            motion: step.motion,
            depth: step.depth,
            fade: 1,
        };
    }

    return {
        ...getFocusGesture(step),
        fade: getFocusFade(step.phase, step.stage),
    };
};

export const project = (
    point: Point3,
    camera: Camera,
    fixed = false
): Point2 => {
    const angle = fixed ? REST_YAW : camera.yaw;
    const tilt = fixed ? REST_PITCH : camera.pitch;
    let x = point[0] * Math.cos(angle) - point[2] * Math.sin(angle);
    let z = point[0] * Math.sin(angle) + point[2] * Math.cos(angle);

    if (!fixed) {
        x += camera.dx;
        z += camera.dz;
    }

    const y = point[1] - 0.65;
    const vy = y * Math.cos(tilt) - z * Math.sin(tilt);
    const vz = y * Math.sin(tilt) + z * Math.cos(tilt);
    const scale = FOCAL / ((fixed ? REST_DISTANCE : camera.distance) - vz);
    const offsetY = fixed ? 0 : camera.dy;

    return [
        VIEW_CENTRE[0] + x * scale,
        VIEW_CENTRE[1] - (vy + offsetY) * scale,
    ];
};

const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

const originScale = (camera: Camera) => {
    const vz =
        -0.65 * Math.sin(camera.pitch) + camera.dz * Math.cos(camera.pitch);

    return FOCAL / (camera.distance - vz);
};

export const orbitCamera = (
    camera: Camera,
    deltaX: number,
    deltaY: number,
    height: number
): Camera => ({
    ...camera,
    yaw: camera.yaw - (2 * Math.PI * deltaX) / height,
    pitch: clamp(
        camera.pitch + (2 * Math.PI * deltaY) / height,
        LIMITS.minPitch,
        LIMITS.maxPitch
    ),
});

export const panCamera = (
    camera: Camera,
    deltaX: number,
    deltaY: number,
    width: number
): Camera => {
    const unit = VIEWBOX_WIDTH / width / originScale(camera);

    return {
        ...camera,
        dx: clamp(camera.dx + deltaX * unit, -LIMITS.maxPan, LIMITS.maxPan),
        dy: clamp(camera.dy - deltaY * unit, -LIMITS.maxPan, LIMITS.maxPan),
    };
};

export const zoomCamera = (camera: Camera, factor: number): Camera => ({
    ...camera,
    distance: clamp(
        camera.distance * factor,
        Math.min(LIMITS.minDistance, camera.distance),
        LIMITS.maxDistance
    ),
});

export const lerpCamera = (from: Camera, to: Camera, t: number): Camera => {
    const mix = (a: number, b: number) => a + (b - a) * t;
    const turn = to.yaw - from.yaw;
    const shortestTurn = turn - 2 * Math.PI * Math.round(turn / (2 * Math.PI));

    return {
        yaw: from.yaw + shortestTurn * t,
        pitch: mix(from.pitch, to.pitch),
        dx: mix(from.dx, to.dx),
        dy: mix(from.dy, to.dy),
        dz: mix(from.dz, to.dz),
        distance: mix(from.distance, to.distance),
    };
};

const toPoints = (list: Point3[], camera: Camera, fixed = false) =>
    list
        .map((point) =>
            project(point, camera, fixed)
                .map((n) => n.toFixed(2))
                .join(',')
        )
        .join(' ');

export const buildGroundPath = (camera: Camera, follow = false) => {
    const path: string[] = [];
    const extent = follow ? 2 : 3;

    for (let i = -extent; i <= extent; i++) {
        const lines: [Point3, Point3][] = [
            [
                [i, -0.045, -extent],
                [i, -0.045, extent],
            ],
            [
                [-extent, -0.045, i],
                [extent, -0.045, i],
            ],
        ];

        lines.forEach((line) => {
            const [a, b] = line.map((point) => project(point, camera, !follow));
            path.push(`M${a.join(',')}L${b.join(',')}`);
        });
    }

    return path.join(' ');
};

interface KitchenFace {
    vertices: Point3[];
    material: string;
    details: Point3[][];
    depth: number;
}

const buildKitchenFaces = (camera: Camera, focus: boolean) => {
    const faces: KitchenFace[] = [];
    const cameraDepth = (p: Point3) =>
        (p[1] - 0.65) * Math.sin(camera.pitch) +
        (p[0] * Math.sin(camera.yaw) +
            p[2] * Math.cos(camera.yaw) +
            camera.dz) *
            Math.cos(camera.pitch);

    const addFace = (
        vertices: Point3[],
        material: string,
        details: Point3[][] = []
    ) => {
        faces.push({
            vertices,
            material,
            details,
            depth:
                vertices.reduce((sum, p) => sum + cameraDepth(p), 0) /
                vertices.length,
        });
    };

    const cabinetBox = (
        x0: number,
        x1: number,
        y0: number,
        y1: number,
        z0: number,
        z1: number,
        material = 'cabinet-side',
        front: 'x' | 'z' | null = null,
        drawers = false
    ) => {
        const face = (vertices: Point3[], name: string) => {
            const details: Point3[][] = [];
            const isFront = name === front;

            if (isFront) {
                const panel = (u: number, v: number): Point3 =>
                    name === 'z'
                        ? [x0 + (x1 - x0) * u, y0 + (y1 - y0) * v, z1]
                        : [x1, y0 + (y1 - y0) * v, z0 + (z1 - z0) * u];

                if (drawers) {
                    [0.34, 0.68].forEach((v) =>
                        details.push([panel(0.035, v), panel(0.965, v)])
                    );
                    [0.23, 0.57, 0.89].forEach((v) =>
                        details.push([panel(0.38, v), panel(0.62, v)])
                    );
                } else {
                    details.push([panel(0.38, 0.88), panel(0.62, 0.88)]);
                }
            }

            const frontClass =
                focus && drawers
                    ? 'cabinet-front focus-cabinet'
                    : 'cabinet-front';

            addFace(vertices, isFront ? frontClass : material, details);
        };

        face(
            [
                [x0, y0, z0],
                [x1, y0, z0],
                [x1, y1, z0],
                [x0, y1, z0],
            ],
            'back'
        );
        face(
            [
                [x0, y0, z0],
                [x0, y0, z1],
                [x0, y1, z1],
                [x0, y1, z0],
            ],
            'left'
        );
        face(
            [
                [x0, y0, z1],
                [x1, y0, z1],
                [x1, y1, z1],
                [x0, y1, z1],
            ],
            'z'
        );
        face(
            [
                [x1, y0, z0],
                [x1, y0, z1],
                [x1, y1, z1],
                [x1, y1, z0],
            ],
            'x'
        );

        if (material === 'benchtop') {
            face(
                [
                    [x0, y1, z0],
                    [x1, y1, z0],
                    [x1, y1, z1],
                    [x0, y1, z1],
                ],
                'top'
            );
        }
    };

    cabinetBox(-0.9, -0.43, 0.11, 0.62, -0.9, -0.43);

    for (let i = 0; i < 3; i++) {
        const x = -0.43 + i * 0.43;
        cabinetBox(
            x + 0.009,
            x + 0.421,
            0.11,
            0.62,
            -0.9,
            -0.43,
            'cabinet-side',
            'z',
            i === 2
        );
    }

    for (let i = 0; i < 2; i++) {
        const z = -0.43 + i * 0.53;
        cabinetBox(
            -0.9,
            -0.43,
            0.11,
            0.62,
            z + 0.009,
            z + 0.521,
            'cabinet-side',
            'x'
        );
    }

    cabinetBox(-0.9, 0.86, 0.035, 0.11, -0.87, -0.48, 'kickboard');
    cabinetBox(-0.87, -0.48, 0.035, 0.11, -0.43, 0.62, 'kickboard');
    cabinetBox(-0.92, 0.89, 0.62, 0.665, -0.92, -0.405, 'benchtop');
    cabinetBox(-0.92, -0.405, 0.62, 0.665, -0.405, 0.66, 'benchtop');

    const layer = (face: KitchenFace) =>
        face.material === 'benchtop'
            ? 2
            : face.material === 'kickboard'
              ? 0
              : 1;

    return faces.sort((a, b) => layer(a) - layer(b) || a.depth - b.depth);
};

export const buildRoomFaces = (camera: Camera, focus = false): Shape[] => {
    const shapes: Shape[] = [];
    const polygon = (list: Point3[], className: string) =>
        shapes.push({
            tag: 'polygon',
            points: toPoints(list, camera),
            className,
        });
    const polyline = (list: Point3[], className: string, dashed = false) =>
        shapes.push({
            tag: 'polyline',
            points: toPoints(list, camera),
            className,
            dashed,
        });

    polygon(
        [
            [-1, 0, -1],
            [1, 0, -1],
            [1, 0, 1],
            [-1, 0, 1],
        ],
        'room-floor'
    );

    for (let i = -0.5; i < 1; i += 0.5) {
        polyline(
            [
                [i, 0, -1],
                [i, 0, 1],
            ],
            'grid'
        );
        polyline(
            [
                [-1, 0, i],
                [1, 0, i],
            ],
            'grid'
        );
    }

    polygon(
        [
            [-1, 0, 1],
            [-1, 0, -1],
            [-1, 1.8, -1],
            [-1, 1.8, 1],
        ],
        'room-wall'
    );
    polygon(
        [
            [-1, 0, -1],
            [1, 0, -1],
            [1, 1.8, -1],
            [-1, 1.8, -1],
        ],
        'room-wall'
    );

    buildKitchenFaces(camera, focus).forEach((face) => {
        polygon(face.vertices, face.material);
        face.details.forEach((line) => polyline(line, 'cabinet-detail'));
    });

    polyline(
        [
            [-1, 1.8, 1],
            [1, 1.8, 1],
            [1, 1.8, -1],
        ],
        'edge',
        true
    );
    polyline(
        [
            [1, 0, 1],
            [1, 1.8, 1],
        ],
        'edge',
        true
    );

    return shapes;
};

export const getRoomCentre = (camera: Camera) => project([0, 0.005, 0], camera);

const MOUSE_TITLES = [
    'Left-click + drag to orbit',
    'Right-click + drag to pan',
    'Scroll to zoom',
];
const TOUCH_TITLES = [
    'One finger to orbit',
    'Two fingers to pan',
    'Pinch to zoom',
];

const FOCUS_TITLES = [
    'Focus on a cabinet',
    '1. Orbit to face the cabinet',
    '2. Pan it into the centre',
    '3. Zoom in for a closer look',
    'Your cabinet, up close',
    'Back to the room',
];

const getFocusCaptions = (touch: boolean) => [
    'Follow the highlighted drawer cabinet',
    touch ? 'Drag with one finger' : 'Left-click + drag',
    touch
        ? 'Drag left and up with two fingers'
        : 'Right-click + drag left and up',
    touch ? 'Spread two fingers to zoom in' : 'Scroll to zoom in',
    'Orbit → Pan → Zoom',
    'Replay any step below',
];

export const getCopy = (
    index: number,
    depth: boolean,
    touch: boolean,
    stage = 0
) => {
    if (index === FOCUS_STEP) {
        return {
            title: FOCUS_TITLES.at(stage) ?? '',
            caption: getFocusCaptions(touch).at(stage) ?? '',
        };
    }

    const captions = [
        'Look around your room',
        depth
            ? 'Drag up / down to move into / out of the room'
            : 'Drag left / right to move across the room',
        touch
            ? 'Spread to zoom in. Pinch to zoom out.'
            : 'Roll the wheel to move closer or further away',
    ];

    return {
        title: (touch ? TOUCH_TITLES : MOUSE_TITLES).at(index) ?? '',
        caption: captions.at(index) ?? '',
    };
};
